import { useState, Suspense, lazy } from 'react';
import { ChevronDown } from 'lucide-react';
import { useDemoTheme } from './DemoThemeContext';

const MonacoEditor = lazy(() => import('@monaco-editor/react'));

const LANGUAGES = [
  { id: 'javascript', label: 'JavaScript' },
  { id: 'python',     label: 'Python 3'   },
  { id: 'typescript', label: 'TypeScript'  },
  { id: 'java',       label: 'Java'        },
  { id: 'cpp',        label: 'C++'         },
];

export default function CodingQuestion({ question, codeAnswer, onCodeChange }) {
  const { isDark } = useDemoTheme();
  const [language, setLanguage] = useState(question.language || 'javascript');
  const [showLang, setShowLang] = useState(false);
  const code = codeAnswer?.code ?? question.starterCode ?? '';

  const handleCode = (val) => onCodeChange(question.id, { code: val || '', language });
  const handleLang = (lang) => { setLanguage(lang); setShowLang(false); onCodeChange(question.id, { code, language: lang }); };
  const selectedLang = LANGUAGES.find((l) => l.id === language) || LANGUAGES[0];

  return (
    <div className="space-y-3" data-testid={`coding-question-${question.id}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2.5 rounded-t-xl"
        style={{ background: 'var(--d-editor-bar)', border: '1px solid var(--d-border)', borderBottom: 'none' }}>
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/60" />
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button data-testid="language-selector-btn" onClick={() => setShowLang(!showLang)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono transition-colors"
              style={{ background: 'var(--d-hover)', border: '1px solid var(--d-border)', color: 'var(--d-text-1)' }}>
              {selectedLang.label}<ChevronDown size={11} />
            </button>
            {showLang && (
              <div className="absolute right-0 top-full mt-1.5 w-36 rounded-xl overflow-hidden z-20"
                style={{ background: 'var(--d-surface)', border: '1px solid var(--d-border-str)', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                {LANGUAGES.map((lang) => (
                  <button key={lang.id} onClick={() => handleLang(lang.id)}
                    className="w-full text-left px-3 py-2.5 text-xs font-mono transition-colors block"
                    style={{ color: lang.id === language ? '#7c3aed' : 'var(--d-text-2)', background: lang.id === language ? 'rgba(124,58,237,0.1)' : 'transparent' }}
                    onMouseEnter={(e) => { if (lang.id !== language) e.currentTarget.style.background = 'var(--d-hover)'; }}
                    onMouseLeave={(e) => { if (lang.id !== language) e.currentTarget.style.background = 'transparent'; }}>
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <span className="text-xs font-mono px-2.5 py-1 rounded-md"
            style={{ background: 'rgba(34,197,94,0.1)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.2)' }}>
            Auto-save
          </span>
        </div>
      </div>

      {/* Editor */}
      <div style={{ border: '1px solid var(--d-border)', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
        <Suspense fallback={
          <div className="flex items-center justify-center h-64 text-xs font-mono"
            style={{ background: 'var(--d-editor-bar)', color: 'var(--d-text-3)' }}>
            Loading editor...
          </div>
        }>
          <MonacoEditor
            height="320px"
            theme={isDark ? 'vs-dark' : 'vs-light'}
            language={language}
            value={code}
            onChange={handleCode}
            options={{
              minimap: { enabled: false },
              fontSize: 13, lineHeight: 22,
              fontFamily: "'JetBrains Mono','Cascadia Code','Fira Code',monospace",
              fontLigatures: true,
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2, wordWrap: 'on',
              lineNumbers: 'on',
              renderLineHighlight: 'line',
              cursorBlinking: 'smooth',
              cursorSmoothCaretAnimation: 'on',
              smoothScrolling: true,
              padding: { top: 12, bottom: 12 },
            }}
          />
        </Suspense>
      </div>
      <p className="text-xs" style={{ color: 'var(--d-text-3)' }}>Write clean, readable code. Comments encouraged. Auto-saved as you type.</p>
    </div>
  );
}
